import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Button from '@/components/UI/Button';
import { _notNil } from '@/littledash';
import { type Animal } from '@/model/Animal.model';
import type { Cage, CageApiId } from '@/model/Cage.model';
import { ApiService } from '@/support/ApiService';
import { useFetchCollection } from '@/support/Hooks/fetch';
import { featuresSelector } from '@/support/Selectors';
import { modalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import cn from 'classnames';
import { type FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MAX_CAGE_POPULATION } from '@/constants/cage';

interface UndoEuthaniseProps {
  subjects: Array<Animal>;
  handleCallback: (message: string) => void;
}

interface UndoEuthaniseFormProps {
  reason_for_change: string;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
}));

/**
 * Modal for undoing an animal euthanisation (moving the animal back into a cage).
 * When the `reason_for_change` feature flag is enabled, selecting a cage advances to
 * a second step that captures a required reason for change before submitting. When the
 * flag is disabled, selecting a cage submits immediately (legacy behaviour).
 */
const UndoEuthanise: FC<UndoEuthaniseProps> = ({ subjects, handleCallback }) => {
  const maxSubjects = MAX_CAGE_POPULATION;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedCageApiId, setSelectedCageApiId] = useState<CageApiId | undefined>(undefined);

  const { reasonForChange } = useSelector(featureSelector);

  const formMethods = useForm<UndoEuthaniseFormProps>({
    mode: 'onChange',
    defaultValues: { reason_for_change: '' },
  });
  const { handleSubmit } = formMethods;

  const dispatch = useDispatch();
  const { closeModal } = modalAction(dispatch);

  const { id } = useParams();
  const {
    collection: collections,
    collectionLoading,
    collectionError,
  } = useFetchCollection<Cage>({
    collectionType: 'studyCages',
    params: { studyId: id },
    includes: 'animals_count',
    queryParams: {
      perPage: -1,
      sort: 'cage_order',
    },
  });

  let what, subjectsToMove;
  if (subjects.length > 1) {
    subjectsToMove = subjects.length;
    what = `these ${subjectsToMove} animals`;
  } else {
    what = 'this animal';
    subjectsToMove = 1;
  }

  const submitUnmarkAsDeceased = async (cageApiId: CageApiId, reason?: string) => {
    const animalApiIds = subjects.map((s) => s.api_id).filter(_notNil);
    if (animalApiIds.length === 0) {
      return;
    }

    setLoading(true);
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/animals/undo-terminate',
      body: {
        animal_api_ids: animalApiIds,
        cage_api_id: cageApiId,
        reason_for_change: reasonForChange ? (reason ?? null) : undefined,
      },
    });
    if (response.type === 'error') {
      setLoading(false);
      setError(response.error);
      return;
    }
    handleCallback('Successfully moved and unmarked as deceased');
    setLoading(false);
  };

  const onCageClick = (cageApiId: CageApiId) => {
    if (reasonForChange) {
      setSelectedCageApiId(cageApiId);
      return;
    }
    submitUnmarkAsDeceased(cageApiId);
  };

  const onReasonSubmit = (form: UndoEuthaniseFormProps) => {
    if (_notNil(selectedCageApiId)) {
      submitUnmarkAsDeceased(selectedCageApiId, form.reason_for_change);
    }
  };

  if (collectionLoading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  if (reasonForChange && _notNil(selectedCageApiId)) {
    return (
      <FormProvider {...formMethods}>
        <form
          className={cn('ui__narrow-modal', { 'disabled-form': loading })}
          onSubmit={handleSubmit(onReasonSubmit)}
          data-testid="undo-euthanise__reason-step"
        >
          <div className="pb3 tc">
            <h3 className="normal f4 pb2">Undo euthanisation for {what}?</h3>
          </div>
          {error && <ApiErrorBanner error={error} />}
          <div className="pb3">
            <ReasonForChange />
          </div>
          <div className="pt2 bt b--silver">
            <Button submit className="w-100 mt2" disabled={loading} testId="undo-euthanise__reason-submit">
              Confirm
            </Button>
            <a
              className={cn('button plain db w-100 mt2', { 'disabled-link': loading })}
              onClick={() => setSelectedCageApiId(undefined)}
            >
              Back
            </a>
          </div>
        </form>
      </FormProvider>
    );
  }

  return (
    <form className={cn('ui__narrow-modal', { 'disabled-form': loading })}>
      <div className="pb3 tc">
        <h3 className="normal f4 pb2">Undo euthanisation for {what}?</h3>
        <p>Please select a cage - each cage can hold up to {maxSubjects} animals</p>
      </div>
      {collectionError && <ApiErrorBanner errorType={ApiErrorType.FETCH} error={collectionError} />}
      {error && <ApiErrorBanner error={error} />}
      <ul className="ui-card-list touch overflow-y-scroll group-list">
        {collections?.map((collection) => {
          const isCurrentCollection = !Array.isArray(subjects);
          return (collection.animals_count as number) + subjectsToMove > maxSubjects ? (
            <FullCollection subjectCount={collection.animals_count ?? 0} key={collection.id} name={collection.name} />
          ) : (
            <AvailableCollection
              collection={collection}
              isCurrentCollection={isCurrentCollection}
              subjectCount={collection.animals_count ?? 0}
              key={collection.id}
              onClick={onCageClick}
            />
          );
        })}
      </ul>
      <div className="pt2 bt b--silver">
        <a className={cn('button plain db w-100 mt2', { 'disabled-link': loading })} onClick={() => closeModal()}>
          Cancel
        </a>
      </div>
    </form>
  );
};

const FullCollection: FC<{ name: string; subjectCount: number }> = ({ name, subjectCount }) => (
  <li>
    <a className="disabled-link">
      <div className="flex justify-between">
        <span>{name}</span>
        <span className="red">{subjectCount}</span>
      </div>
    </a>
  </li>
);

const AvailableCollection: FC<{
  collection: Cage;
  isCurrentCollection: boolean;
  subjectCount: number;
  onClick: (cageApiId: CageApiId) => void;
}> = ({ collection, isCurrentCollection, subjectCount, onClick }) => (
  <li>
    <a onClick={() => onClick(collection.api_id)} className={`${isCurrentCollection && 'disabled-link active-box'}`}>
      <div className="flex justify-between">
        <span>{collection.name}</span>
        <span className="mid-gray">{subjectCount}</span>
      </div>
    </a>
  </li>
);

export default UndoEuthanise;
